import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';

const API_BASE = import.meta.env.VITE_API_URL ?? '';

interface Account {
  id: string;
  name: string;
}

interface SheetConnection {
  id: string;
  name: string;
  serviceAccountEmail: string;
  isActive: boolean;
  authType: 'oauth' | 'service_account';
  createdAt: string;
}

interface YearWorkbook {
  id: string;
  year: number;
  googleConnectionId: string;
  spreadsheetId: string;
  spreadsheetUrl: string | null;
  createdAt: string;
}

interface TestResult {
  success: boolean;
  serviceAccountEmail?: string;
  hasValidToken?: boolean;
  spreadsheetTitle?: string;
  sheets?: string[];
  error?: string;
}

interface TabDiscoveryResult {
  title: string;
  tabs: string[];
}

interface OAuthConfig {
  configured: boolean;
  clientId?: string;
}

export function Sheets() {
  const [connections, setConnections] = useState<SheetConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // OAuth config state
  const [oauthConfig, setOauthConfig] = useState<OAuthConfig | null>(null);
  const [configClientId, setConfigClientId] = useState('');
  const [configClientSecret, setConfigClientSecret] = useState('');
  const [savingConfig, setSavingConfig] = useState(false);
  const [showConfigForm, setShowConfigForm] = useState(false);

  // Per-connection state
  const [testing, setTesting] = useState<Set<string>>(new Set());
  const [testResults, setTestResults] = useState<Record<string, TestResult>>({});
  const [spreadsheetInputs, setSpreadsheetInputs] = useState<Record<string, string>>({});
  const [loadingTabs, setLoadingTabs] = useState<Set<string>>(new Set());
  const [tabResults, setTabResults] = useState<Record<string, TabDiscoveryResult | { error: string }>>({});

  // Year workbook state
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [workbooks, setWorkbooks] = useState<YearWorkbook[]>([]);
  const [creatingWorkbook, setCreatingWorkbook] = useState(false);
  const [workbookMessage, setWorkbookMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  async function loadConnections() {
    try {
      setError('');
      const [res, config, accts] = await Promise.all([
        api.get<{ connections: SheetConnection[]; total: number }>('/sheets'),
        api.get<OAuthConfig>('/google/config'),
        api.get<Account[]>('/accounts'),
      ]);
      setConnections(res.connections);
      setOauthConfig(config);
      setAccounts(accts);

      // Load year workbooks for the first account (if any)
      if (accts.length > 0) {
        try {
          const wb = await api.get<{ workbooks: YearWorkbook[] }>(`/sheets/year-workbooks/${accts[0].id}`);
          setWorkbooks(wb.workbooks);
        } catch {
          // Non-fatal — year_workbooks table may not exist yet
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load connections');
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveConfig() {
    if (!configClientId.trim() || !configClientSecret.trim()) return;
    setSavingConfig(true);
    setError('');
    try {
      await api.put('/google/config', {
        clientId: configClientId.trim(),
        clientSecret: configClientSecret.trim(),
      });
      setOauthConfig({ configured: true, clientId: configClientId.trim().slice(0, 20) + '...' });
      setSuccessMessage('Google OAuth configured successfully!');
      setShowConfigForm(false);
      setConfigClientSecret('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save OAuth config');
    } finally {
      setSavingConfig(false);
    }
  }

  useEffect(() => {
    // Check for OAuth redirect result
    const params = new URLSearchParams(window.location.search);
    if (params.get('connected') === 'true') {
      setSuccessMessage('Google account connected successfully!');
      window.history.replaceState({}, '', window.location.pathname);
    }
    const oauthError = params.get('error');
    if (oauthError) {
      setError(oauthError);
      window.history.replaceState({}, '', window.location.pathname);
    }

    loadConnections();
  }, []);

  function handleConnectGoogle() {
    // Navigate to the OAuth flow — this is a full-page redirect
    window.location.href = `${API_BASE}/api/google/auth`;
  }

  function toggleExpanded(id: string) {
    setExpandedId((prev) => (prev === id ? null : id));
  }

  async function handleTest(id: string) {
    setTesting((prev) => new Set(prev).add(id));
    setTestResults((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    try {
      const result = await api.post<TestResult>(`/sheets/${id}/test`);
      setTestResults((prev) => ({ ...prev, [id]: result }));
      await loadConnections();
    } catch (err) {
      setTestResults((prev) => ({
        ...prev,
        [id]: { success: false, error: err instanceof Error ? err.message : 'Test failed' },
      }));
    } finally {
      setTesting((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }

  async function handleLoadTabs(id: string) {
    const spreadsheetId = spreadsheetInputs[id]?.trim();
    if (!spreadsheetId) return;

    setLoadingTabs((prev) => new Set(prev).add(id));
    setTabResults((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });

    try {
      const result = await api.get<TabDiscoveryResult>(
        `/sheets/${id}/spreadsheet-tabs?spreadsheet_id=${encodeURIComponent(spreadsheetId)}`,
      );
      setTabResults((prev) => ({ ...prev, [id]: result }));
    } catch (err) {
      setTabResults((prev) => ({
        ...prev,
        [id]: { error: err instanceof Error ? err.message : 'Failed to load tabs' },
      }));
    } finally {
      setLoadingTabs((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }

  async function handleDelete(conn: SheetConnection) {
    if (!confirm(`Delete connection "${conn.name}"? Exports using this connection will stop working.`)) return;
    try {
      await api.delete(`/sheets/${conn.id}`);
      // Clear expanded state if we're deleting the expanded card
      if (expandedId === conn.id) setExpandedId(null);
      await loadConnections();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete connection');
    }
  }

  async function handleCreateWorkbook() {
    if (accounts.length === 0 || connections.length === 0) return;
    const accountId = accounts[0].id;
    const connectionId = connections[0].id;
    const currentYear = new Date().getUTCFullYear();

    setCreatingWorkbook(true);
    setWorkbookMessage(null);
    try {
      const result = await api.post<{ id: string; spreadsheetId: string; spreadsheetUrl: string; tabs: string[]; year: number }>(
        `/sheets/${connectionId}/create-year-workbook`,
        { accountId, year: currentYear },
      );
      setWorkbookMessage({
        type: 'success',
        text: `Created "Sellvana ${result.year}" with ${result.tabs.length} tabs`,
      });
      // Reload workbooks
      const wb = await api.get<{ workbooks: YearWorkbook[] }>(`/sheets/year-workbooks/${accountId}`);
      setWorkbooks(wb.workbooks);
    } catch (err) {
      setWorkbookMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to create workbook',
      });
    } finally {
      setCreatingWorkbook(false);
    }
  }

  if (loading) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold">Google Sheets Connections</h2>
          <p className="text-sm text-gray-500">Connect your Google account to enable data sync to Sheets.</p>
        </div>
        <button
          onClick={handleConnectGoogle}
          className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
        >
          <GoogleIcon />
          Connect Google Account
        </button>
      </div>

      {successMessage && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-3">
          <p className="text-sm text-green-700">{successMessage}</p>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* OAuth Config Section */}
      {oauthConfig && !oauthConfig.configured && !showConfigForm && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <h3 className="text-sm font-medium text-amber-800">Google OAuth Setup Required</h3>
          <p className="mt-1 text-xs text-amber-700">
            Enter your Google OAuth Client ID and Secret to enable Google Sheets connectivity.
          </p>
          <button
            onClick={() => setShowConfigForm(true)}
            className="mt-3 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700"
          >
            Configure OAuth
          </button>
        </div>
      )}

      {(showConfigForm || (oauthConfig?.configured && showConfigForm)) && (
        <div className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Google OAuth Configuration</h3>
            {oauthConfig?.configured && (
              <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                Configured
              </span>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700">Client ID</label>
            <input
              type="text"
              value={configClientId}
              onChange={(e) => setConfigClientId(e.target.value)}
              placeholder="123456789.apps.googleusercontent.com"
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700">Client Secret</label>
            <input
              type="password"
              value={configClientSecret}
              onChange={(e) => setConfigClientSecret(e.target.value)}
              placeholder="GOCSPX-..."
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleSaveConfig}
              disabled={savingConfig || !configClientId.trim() || !configClientSecret.trim()}
              className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {savingConfig ? 'Saving...' : 'Save Configuration'}
            </button>
            <button
              onClick={() => setShowConfigForm(false)}
              className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Reconfigure link when already configured */}
      {oauthConfig?.configured && !showConfigForm && (
        <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-2">
          <p className="text-xs text-gray-500">
            OAuth configured (Client ID: {oauthConfig.clientId})
          </p>
          <button
            onClick={() => setShowConfigForm(true)}
            className="text-xs font-medium text-brand-600 hover:text-brand-700"
          >
            Reconfigure
          </button>
        </div>
      )}

      {connections.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center">
          <SheetsIcon />
          <p className="mt-2 text-sm font-medium text-gray-900">No connections yet</p>
          <p className="mt-1 text-xs text-gray-500">
            Connect your Google account to start syncing Amazon data to Google Sheets.
          </p>
          <button
            onClick={handleConnectGoogle}
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
          >
            <GoogleIcon />
            Connect Google Account
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {connections.map((conn) => {
            const isExpanded = expandedId === conn.id;
            const testResult = testResults[conn.id];
            const tabResult = tabResults[conn.id];
            const isLoadingTabs = loadingTabs.has(conn.id);

            return (
              <div
                key={conn.id}
                className="rounded-xl border border-gray-200 bg-white overflow-hidden"
              >
                {/* Connection header — always visible, clickable to expand */}
                <div
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleExpanded(conn.id)}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium">{conn.name}</p>
                      <ConnectionStatusBadge status={conn.isActive ? 'active' : 'untested'} />
                      <AuthTypeBadge authType={conn.authType} />
                    </div>
                    <p className="mt-1 text-xs text-gray-500 truncate">{conn.serviceAccountEmail}</p>
                    <p className="mt-0.5 text-xs text-gray-400">
                      Created {new Date(conn.createdAt).toLocaleDateString()}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(conn);
                      }}
                      className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
                    >
                      Delete
                    </button>
                    <ChevronIcon expanded={isExpanded} />
                  </div>
                </div>

                {/* Expandable panel */}
                {isExpanded && (
                  <div className="border-t border-gray-100 px-4 pb-4 space-y-4">
                    {/* Test Connection section */}
                    <div className="pt-3">
                      <div className="flex items-center justify-between">
                        <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                          Test Connection
                        </h4>
                        <button
                          onClick={() => handleTest(conn.id)}
                          disabled={testing.has(conn.id)}
                          className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                        >
                          {testing.has(conn.id) ? 'Testing...' : 'Run Test'}
                        </button>
                      </div>
                      {testResult && (
                        <div
                          className={`mt-2 rounded-lg border p-3 text-xs ${
                            testResult.success
                              ? 'border-green-200 bg-green-50 text-green-700'
                              : 'border-red-200 bg-red-50 text-red-600'
                          }`}
                        >
                          {testResult.success ? (
                            <div className="space-y-1">
                              <p className="font-medium">Connection successful</p>
                              {testResult.serviceAccountEmail && (
                                <p>Account: {testResult.serviceAccountEmail}</p>
                              )}
                              {testResult.hasValidToken !== undefined && (
                                <p>Token valid: {testResult.hasValidToken ? 'Yes' : 'No'}</p>
                              )}
                            </div>
                          ) : (
                            <p>{testResult.error}</p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Spreadsheet Tab Loader section */}
                    <div>
                      <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                        Spreadsheet Tab Discovery
                      </h4>
                      <p className="text-xs text-gray-500 mb-2">
                        Enter a spreadsheet ID to verify access and discover available tabs.
                      </p>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Spreadsheet ID (e.g. 1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgVE2upms)"
                          value={spreadsheetInputs[conn.id] ?? ''}
                          onChange={(e) =>
                            setSpreadsheetInputs((prev) => ({ ...prev, [conn.id]: e.target.value }))
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleLoadTabs(conn.id);
                          }}
                          className="flex-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-700 placeholder-gray-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                        />
                        <button
                          onClick={() => handleLoadTabs(conn.id)}
                          disabled={isLoadingTabs || !spreadsheetInputs[conn.id]?.trim()}
                          className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap"
                        >
                          {isLoadingTabs ? 'Loading...' : 'Load Tabs'}
                        </button>
                      </div>

                      {tabResult && (
                        <div className="mt-2">
                          {'error' in tabResult ? (
                            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-600">
                              {tabResult.error}
                            </div>
                          ) : (
                            <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-xs text-green-700">
                              <p className="font-medium">{tabResult.title}</p>
                              <div className="mt-1.5 flex flex-wrap gap-1.5">
                                {tabResult.tabs.map((tab) => (
                                  <span
                                    key={tab}
                                    className="inline-flex items-center rounded-md bg-white border border-green-200 px-2 py-0.5 text-xs font-medium text-green-800"
                                  >
                                    {tab}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Year Workbooks section */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold">Year Workbooks</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              One spreadsheet per year with all data sources as tabs. Auto-sync populates them on every cron run.
            </p>
          </div>
          {connections.length > 0 && accounts.length > 0 && (
            <button
              onClick={handleCreateWorkbook}
              disabled={creatingWorkbook || workbooks.some(w => w.year === new Date().getUTCFullYear())}
              className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {creatingWorkbook ? 'Creating...' : `Create ${new Date().getUTCFullYear()} Workbook`}
            </button>
          )}
        </div>

        {workbookMessage && (
          <div className={`rounded-lg border p-3 text-sm ${
            workbookMessage.type === 'success'
              ? 'border-green-200 bg-green-50 text-green-700'
              : 'border-red-200 bg-red-50 text-red-600'
          }`}>
            {workbookMessage.text}
          </div>
        )}

        {connections.length === 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700">
            Connect a Google account first to create year workbooks.
          </div>
        )}

        {accounts.length === 0 && connections.length > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700">
            Add an Amazon account in Credentials first.
          </div>
        )}

        {workbooks.length > 0 ? (
          <div className="divide-y divide-gray-100 rounded-lg border border-gray-200">
            {workbooks.map((wb) => (
              <div key={wb.id} className="flex items-center justify-between px-4 py-3">
                <div>
                  <p className="text-sm font-medium">Sellvana {wb.year}</p>
                  <p className="text-xs text-gray-500">Created {new Date(wb.createdAt).toLocaleDateString()}</p>
                </div>
                <a
                  href={wb.spreadsheetUrl ?? `https://docs.google.com/spreadsheets/d/${wb.spreadsheetId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-brand-600 hover:bg-gray-50"
                >
                  Open in Sheets
                </a>
              </div>
            ))}
          </div>
        ) : connections.length > 0 && accounts.length > 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center">
            <p className="text-sm text-gray-500">No year workbooks yet</p>
            <p className="text-xs text-gray-400 mt-1">
              Create your first workbook to start mirroring data to Google Sheets automatically.
            </p>
          </div>
        ) : null}
      </div>

      {/* Info box */}
      <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
        <h3 className="text-sm font-medium text-blue-800">How Google Sheets access works</h3>
        <ol className="mt-2 space-y-1 text-xs text-blue-700 list-decimal list-inside">
          <li>Click "Connect Google Account" to sign in with your Google account</li>
          <li>Grant Sellvana permission to manage spreadsheets and create files</li>
          <li>Your connection will appear above -- use it when creating data exports</li>
          <li>Sellvana can only access spreadsheets you own or that are shared with you</li>
        </ol>
      </div>
    </div>
  );
}

function ConnectionStatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    active: 'bg-green-100 text-green-700',
    error: 'bg-red-100 text-red-700',
    untested: 'bg-yellow-100 text-yellow-700',
  };
  const labels: Record<string, string> = {
    active: 'Active',
    error: 'Error',
    untested: 'Not tested',
  };
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${styles[status] ?? styles.untested}`}>
      {labels[status] ?? status}
    </span>
  );
}

function AuthTypeBadge({ authType }: { authType: string }) {
  if (authType === 'oauth') {
    return (
      <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
        OAuth
      </span>
    );
  }
  return (
    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
      Service Account
    </span>
  );
}

function ChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      className={`h-4 w-4 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#fff" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#fff" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#fff" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#fff" />
    </svg>
  );
}

function SheetsIcon() {
  return (
    <svg className="mx-auto h-10 w-10 text-gray-300" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 01-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0112 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M10.875 12h-7.5m8.625 0h7.5m-7.5 0c.621 0 1.125.504 1.125 1.125M20.625 12c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5M12 14.625v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 14.625c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m0 0v1.5c0 .621-.504 1.125-1.125 1.125" />
    </svg>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="flex items-center justify-between">
        <div>
          <div className="h-5 w-48 rounded bg-gray-200" />
          <div className="mt-2 h-4 w-72 rounded bg-gray-200" />
        </div>
        <div className="h-9 w-36 rounded-lg bg-gray-200" />
      </div>
      {[1, 2].map((i) => (
        <div key={i} className="h-20 rounded-xl bg-gray-200" />
      ))}
    </div>
  );
}
